import { EnvType } from "../services";
import { IApiClient } from "../api-client";
import { ApplyRuleOnObjectsResponse } from "../Types/apply-rule-on-objects-response";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetJobsResponse } from "../Types/get-jobs-response";
import { GetJobResponse } from "../Types/get-job-response";
import { GetRulesResponse } from "../Types/get-rules-response";
import { GetRuleResponse } from "../Types/get-rule-response";
import { ApplyRuleOnObjectsRequest } from "../Types/apply-rule-on-objects-request";
import { DbJobRuleInfos } from "../Types/db-job-rule-infos";
import { JobTypes } from "../Types/job-types";
import { GetRulesMatchingObjectRequest } from "../Types/get-rules-request";
import { AxiosResponseHeaders } from "axios";

interface IJobBrokerApiClient {
  ListJobsWithPage(pageNb: number, pageSize: number): Promise<GetJobsResponse>;
  ListJobsWithType(type: JobTypes): Promise<GetJobsResponse>;
  ListJobsWithTypeAndPage(type: JobTypes, pageNb: number, pageSize: number): Promise<GetJobsResponse>;
  ListJobsWithRuleId(ruleId: number): Promise<GetJobsResponse>;
  ListJobsWithRuleIdAndPage(ruleId: number, pageNb: number, pageSize: number): Promise<GetJobsResponse>;
  GetJob(id: number): Promise<GetJobResponse>;
  ListByObjectId(objectId: number): Promise<GetJobsResponse>;
  SetPriorityOnJobItemById(id: number, priority: number): Promise<GenericResponseStd>;
  Retry(id: number, executeNow: boolean): Promise<GenericResponseStd>;
  Cancel(id: number): Promise<GenericResponseStd>;
  Abort(id: number): Promise<GenericResponseStd>;
  NotifyResync(id: number): Promise<GenericResponseStd>;
  ListRules(): Promise<GetRulesResponse>;
  GetRule(id: number): Promise<GetRuleResponse>;
  ListRulesByObject(objectId: number): Promise<GetRulesResponse>;
  CreateRule(request: DbJobRuleInfos): Promise<GenericResponseStd>;
  UpdateRule(request: DbJobRuleInfos): Promise<GenericResponseStd>;
  DeleteRule(id: number): Promise<GenericResponseStd>;
  ApplyRuleOnObject(ruleId: number, objectId: number): Promise<GenericResponseStd>;
  ApplyRuleOnObjects(request: ApplyRuleOnObjectsRequest): Promise<ApplyRuleOnObjectsResponse>;
  ApplyRuleOnObjectAndFile(ruleId: number, objectId: number, fileId: number): Promise<GenericResponseStd>;
}

export default class JobBrokerApiClient implements IJobBrokerApiClient {
  readonly servicePort = 9513;
  readonly serviceBaseUrl = "job-broker-service/api";

  jobBrokerApiClient: IApiClient;

  constructor(jobBrokerApiClient: IApiClient, envType: EnvType) {
    this.jobBrokerApiClient = jobBrokerApiClient;
    if (envType === "local") this.jobBrokerApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async ListJobsWithPage(pageNb: number, pageSize: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/jobs/list/pages/${pageNb}/${pageSize}`);
  }
  async ListJobsWithType(type: JobTypes): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/jobs/types/${type}`);
  }
  async ListJobsWithTypeAndPage(type: JobTypes, pageNb: number, pageSize: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/jobs/types/${type}/pages/${pageNb}/${pageSize}`);
  }
  async ListJobsWithRuleId(ruleId: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/jobs/rules/${ruleId}`);
  }
  async ListJobsWithRuleIdAndPage(ruleId: number, pageNb: number, pageSize: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/jobs/rules/${ruleId}/pages/${pageNb}/${pageSize}`);
  }
  async GetJob(id: number): Promise<GetJobResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/jobs/${id}`);
  }
  async ListByObjectId(objectId: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/jobs/objects/${objectId}`);
  }
  async SetPriorityOnJobItemById(id: number, priority: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.put(`${this.serviceBaseUrl}/jobs/${id}/priority/${priority}`);
  }
  async Retry(id: number, executeNow: boolean): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.put(`${this.serviceBaseUrl}/jobs/retry/${id}/${executeNow}`);
  }
  async Cancel(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.put(`${this.serviceBaseUrl}/jobs/cancel/${id}`);
  }
  async Abort(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.put(`${this.serviceBaseUrl}/jobs/abort/${id}`);
  }
  async NotifyResync(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.put(`${this.serviceBaseUrl}/jobs/notify-resync/${id}`);
  }
  async BypassJobResult(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.put(`${this.serviceBaseUrl}/jobs/bypass-result/${id}`);
  }
  async DownloadJobResult(id: number): Promise<[string, AxiosResponseHeaders]> {
    return await this.jobBrokerApiClient.getWithHeaders(`${this.serviceBaseUrl}/jobs/${id}/download`, "arraybuffer");
  }
  async ListRules(): Promise<GetRulesResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/rules`);
  }
  async GetRule(id: number): Promise<GetRuleResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/rules/${id}`);
  }
  async ListRulesByObject(objectId: number): Promise<GetRulesResponse> {
    return await this.jobBrokerApiClient.get(`${this.serviceBaseUrl}/rules/object/${objectId}`);
  }
  async ListRulesByObjects(request: GetRulesMatchingObjectRequest): Promise<GetRulesResponse> {
    return await this.jobBrokerApiClient.post(`${this.serviceBaseUrl}/rules/objects`, request);
  }
  async CreateRule(request: DbJobRuleInfos): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.post(`${this.serviceBaseUrl}/rules`, request);
  }
  async UpdateRule(request: DbJobRuleInfos): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.put(`${this.serviceBaseUrl}/rules`, request);
  }
  async DeleteRule(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.delete(`${this.serviceBaseUrl}/rules/${id}`);
  }
  async ApplyRuleOnObject(ruleId: number, objectId: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.post(`${this.serviceBaseUrl}/rules/${ruleId}/apply/${objectId}`);
  }
  async ApplyRuleOnObjects(request: ApplyRuleOnObjectsRequest): Promise<ApplyRuleOnObjectsResponse> {
    return await this.jobBrokerApiClient.post(`${this.serviceBaseUrl}/rules/apply`, request);
  }
  async ApplyRuleOnObjectAndFile(ruleId: number, objectId: number, fileId: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.post(
      `${this.serviceBaseUrl}/rules/${ruleId}/apply/${objectId}/file/${fileId}`
    );
  }

  //------Utils------
  GetApiUrl(): string {
    return this.jobBrokerApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
